/**
 * StudioWorkspace — the firm-operations hub: Calendar (Staff PTO / Studio Holidays / Board
 * Meetings), Org Chart (Role Profiles), Board (agendas + minutes), Employee Handbook, and
 * Mission & Core Values as tabs of one workspace, instead of each being its own top-level SiteNav
 * item. Matt's call (2026-08) after shipping Role Profiles standalone: role mandates/profiles was
 * "a very strange place" to live alone in the nav, and there's a broader need for a firm-identity
 * home that can grow. Named "Studio" per Matt, not "Firm Ops" or "Operations" — see docs/STUDIO.md.
 * Studio Calendar moved in from its own standalone workspace the same round Board shipped, once
 * the Board's meetings needed a calendar to mirror onto anyway.
 *
 * **Visibility (2026-08, Matt's explicit call): everything except Calendar is admin-only.**
 * Calendar is the one tab every seat type can see (matches its pre-move visibility — every team
 * member logs their own PTO there). Org Chart, Board, Handbook, and Mission & Values are hidden
 * from the tab bar entirely for a non-admin, not just locked for editing — and the corresponding
 * list actions in api/role-profiles.js / api/board-meetings.js / api/studio-documents.js now
 * require the admin module server-side too, so this isn't just a client-side hide. Non-admin data
 * for those three isn't even fetched (see the load effect below) — no point requesting something
 * the API will now refuse anyway.
 *
 * Mirrors CrmWorkspace's shell exactly: docks into the content area beside SiteNav (tray-aware
 * via the `.aj-studio-ws` rule in outline.css — the rule that was MISSING when Role Profiles first
 * shipped as its own standalone panel, which is why it rendered inline instead of full-bleed and
 * the outline showed up stacked underneath it), `ws=studio` + `wsTool` for the active tab,
 * sessionStorage/localStorage persistence, `open-studio`/`close-studio` events. Tab visibility
 * fallback (`visibleTabs.includes(tool) ? tool : visibleTabs[0]`) mirrors CrmWorkspace's own
 * admin-only-tab fallback (Fee Models/References default back to Today for a non-admin).
 *
 * Owns all data loading for its tabs — tab components are pure content, no loading state of their
 * own, matching the "container loads on open" pattern already used by MilestoneSections.
 */

import { useEffect, useState } from 'react'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { listRoleProfiles } from '../services/roleProfiles.js'
import { listStudioDocuments } from '../services/studioDocuments.js'
import { listBoardMeetings } from '../services/boardMeetings.js'
import { listStudioCalendarEvents } from '../services/studioCalendar.js'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import RoleProfilesTab from './RoleProfilesTab.jsx'
import StudioDocumentTab from './StudioDocumentTab.jsx'
import BoardTab from './BoardTab.jsx'
import StudioCalendarTab from './StudioCalendarTab.jsx'

const STORE_KEY = 'aj:studio:ws'
const ADMIN_TABS = ['org-chart', 'board', 'handbook', 'mission-values']
const ALL_TABS = ['calendar', ...ADMIN_TABS]
const TAB_LABEL = { calendar: 'Calendar', 'org-chart': 'Org Chart', board: 'Board', handbook: 'Handbook', 'mission-values': 'Mission & Values' }

function readStored() {
  try {
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    if (ws === 'studio') return { active: true, tool: params.get('wsTool') || 'org-chart' }
    if (ws !== null) return null
    // A peek/split iframe must never auto-restore a workspace from storage shared with the
    // parent tab — see ScheduleIntelWorkspace.jsx's own comment on this same check.
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch { return null }
}

export default function StudioWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [tool, setTool] = useState((stored && stored.tool) || 'org-chart')
  const [seatReady, setSeatReady] = useState(isSeatLoaded)
  const [roles, setRoles] = useState(null)
  const [docs, setDocs] = useState(null)
  const [meetings, setMeetings] = useState(null)
  const [calendarEvents, setCalendarEvents] = useState(null)

  const admin = seatReady && hasModule('admin')

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      if (d.tool) setTool(d.tool)
      setActive(true)
    }
    function onClose() { setActive(false) }
    window.addEventListener('open-studio', onOpen)
    window.addEventListener('close-studio', onClose)
    return () => { window.removeEventListener('open-studio', onOpen); window.removeEventListener('close-studio', onClose) }
  }, [])

  useEffect(() => {
    if (seatReady) return
    const id = setInterval(() => { if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) } }, 100)
    return () => clearInterval(id)
  }, [seatReady])

  useEffect(() => {
    try {
      if (active) {
        const json = JSON.stringify({ active: true, tool })
        sessionStorage.setItem(STORE_KEY, json); localStorage.setItem(STORE_KEY, json)
      } else {
        sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY)
      }
    } catch { /* ignore */ }
  }, [active, tool])

  useEffect(() => {
    const url = new URL(window.location)
    if (active) {
      url.searchParams.set('ws', 'studio'); url.searchParams.set('wsTool', tool)
      document.title = 'Alder James Studio'
    } else if (url.searchParams.get('ws') === 'studio') {
      url.searchParams.delete('ws'); url.searchParams.delete('wsTool')
    }
    window.history.replaceState(null, '', url)
  }, [active, tool])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  useEffect(() => {
    if (!active) return
    if (calendarEvents === null) listStudioCalendarEvents().then(setCalendarEvents).catch(() => setCalendarEvents([]))
    if (!seatReady || !admin) return
    if (roles === null) listRoleProfiles().then(setRoles).catch(() => setRoles([]))
    if (docs === null) listStudioDocuments().then(setDocs).catch(() => setDocs([]))
    if (meetings === null) listBoardMeetings().then(setMeetings).catch(() => setMeetings([]))
  }, [active, seatReady, admin]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null

  const visibleTabs = admin ? ALL_TABS : ['calendar']
  const activeTool = visibleTabs.includes(tool) ? tool : visibleTabs[0]
  const docFor = slug => docs && docs.find(d => d.slug === slug)
  const patchDoc = updated => setDocs(prev => prev.map(d => d.slug === updated.slug ? updated : d))

  return (
    <div className="aj-studio-ws" style={S.panel}>
      <WorkspaceHeader
        title="Studio"
        tabs={visibleTabs.length > 1 && (
          <div style={workspaceTabsWrapStyle}>
            {visibleTabs.map(t => (
              <button key={t} style={workspaceTabStyle(activeTool === t)} onClick={() => setTool(t)}>{TAB_LABEL[t]}</button>
            ))}
          </div>
        )}
      />

      <div style={S.content}>
        {activeTool === 'calendar' && (
          <StudioCalendarTab events={calendarEvents} admin={admin} onEventsChange={setCalendarEvents} />
        )}
        {activeTool === 'org-chart' && (
          <RoleProfilesTab roles={roles} admin={admin} onRolesChange={setRoles} />
        )}
        {activeTool === 'board' && (
          <BoardTab meetings={meetings} admin={admin} onMeetingsChange={setMeetings} />
        )}
        {activeTool === 'handbook' && (
          <StudioDocumentTab doc={docFor('handbook')} admin={admin} onSaved={patchDoc} />
        )}
        {activeTool === 'mission-values' && (
          <StudioDocumentTab doc={docFor('mission_values')} admin={admin} onSaved={patchDoc} />
        )}
      </div>
    </div>
  )
}

const S = {
  panel: { display: 'flex', flexDirection: 'column' },
  // relative: the Org Chart canvas fills this box (position absolute, inset 0).
  content: { flex: 1, overflow: 'auto', background: 'var(--aj-bg-page)', position: 'relative' },
}
